/**
 * AI kill switch, portal side (CC-AI-KILL-001). Mirrors
 * client/services/aiPaused.js: with FEATURE_AI_GENERATION=false the API
 * refuses AI work with HTTP 503 { error, code: 'AI_GENERATION_DISABLED' }, an
 * SSE error event carrying the same code, or a failed turn node that records
 * it. These helpers recognize all three and pick the copy: always the
 * server's own message, with AI_PAUSED_MESSAGE as the fallback.
 *
 * Pure: no imports, so the unit suite runs it directly.
 */

export const AI_GENERATION_DISABLED = 'AI_GENERATION_DISABLED';
export const AI_PAUSED_MESSAGE = 'AI features are paused right now. Please try again later.';

const nonEmpty = (value) => {
  return typeof value === 'string' && value.trim() ? value : null;
};

/** True for an error carrying the 503 body or an SSE event's code, or the raw body / event. */
export function isAiPausedError(err) {
  if (!err || typeof err !== 'object') { return false; }
  return err.code === AI_GENERATION_DISABLED || err.response?.data?.code === AI_GENERATION_DISABLED;
}

/** The user-facing sentence for a paused refusal: the server's, else the default. */
export function aiPausedMessage(err) {
  const serverMessage = nonEmpty(err?.response?.data?.error) || nonEmpty(err?.error) || nonEmpty(err?.message);
  if (!serverMessage || serverMessage === AI_GENERATION_DISABLED || /^Request failed with status code \d+$/.test(serverMessage)) {
    return AI_PAUSED_MESSAGE;
  }
  return serverMessage;
}

/**
 * The paused notice for a turn node, or null when the kill switch did not
 * refuse it. A client-flagged node carries the server's sentence in
 * `aiPaused`; a persisted failed node records the refusal as a code field or
 * in its stored `error` text (internal wording, so it gets the default
 * sentence).
 */
export function aiPausedTurnMessage(node) {
  if (!node || typeof node !== 'object') { return null; }
  if (nonEmpty(node.aiPaused)) { return node.aiPaused; }
  const stored = typeof node.error === 'string' ? node.error : '';
  const paused = node.errorCode === AI_GENERATION_DISABLED
    || node.code === AI_GENERATION_DISABLED
    || stored === AI_PAUSED_MESSAGE
    || /AI_GENERATION_DISABLED|AI generation disabled/.test(stored);
  return paused ? AI_PAUSED_MESSAGE : null;
}
